import React, { FC, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Dropdown } from 'react-bootstrap';
import ApexCharts, { ApexOptions } from 'apexcharts';
import { useThemeMode } from '../../../_myb/partials';
import { getCSSVariableValue } from '../../../_myb/assets/ts/_utils';
import { Content } from '../../../_myb/layout/components/content';
import { ToolbarWrapper } from '../../../_myb/layout/components/toolbar';
import { PageTitle } from '../../../_myb/layout/core';
import { ToolbarContext, defaultToolbarState } from '../../../_myb/layout/components/toolbar/Toolbar';
import { KTIcon } from '../../../_myb/helpers';
import { formatCurrency } from '../../modules/reports/components/ReportHelpers';
import {
  BalancesDTO,
  BudgetDashboardWidget,
  BudgetVsActualRow,
  DashboardNeedsAttentionDTO,
  DashboardResponseV2,
  DateFilter,
  PeriodData,
  ReportFilter,
  SubscriptionData,
} from '../../types';
import {
  fetchAccounts,
  fetchBudgetDashboardWidget,
  fetchBudgetVsActual,
  fetchContacts,
  fetchDashboardBalances,
  fetchDashboardCurrentPeriod,
  fetchDashboardNeedsAttention,
  fetchDashboardPriorPeriod,
  fetchItems,
  fetchSeenFeatures,
  markFeatureSeen,
} from '../../api';
import { brandSettings } from '../../modules/settings/BrandSettings';
import { OnboardingHelper } from './OnboardingHelper';
import { MarketingWidget } from './MarketingWidget';
import AiStatusBriefCard from './AiStatusBriefCard';
import { useNavigate } from 'react-router-dom';
import { globalValue, useAppContext } from '../../modules/apps/AppContext';
import { canAccess } from '../../modules/auth/accessControl';
import { AccessibleTiles } from '../../modules/widgets/components/AccessibleTiles';
import { getEffectiveEdition, canUseModule } from '../../modules/subscription/subscriptionAccess';
import { useBootstrapTooltips } from '../../modules/utils/useBootstrapTooltips';

// ── Visual-only reskin of the Dashboard, restyled to the new mock's visual
// direction (icon-tile eyebrows, inline % pills, monospace amounts, segmented
// expense bar, full-width Budget Tracker). Kept on Bootstrap/Metronic/ApexCharts/
// KTIcon — no new design-system dependency. Promoted to the main /dashboard route;
// the prior default lives on at /dashboard-v2, and /dashboard-v1 is the original.

const DATE_OPTIONS = [
  { value: 'ThisYear', label: 'This Year' },
  { value: 'ThisMonth', label: 'This Month' },
  { value: 'ThisQuarter', label: 'This Quarter' },
  { value: 'LastMonth', label: 'Last Month' },
  { value: 'LastQuarter', label: 'Last Quarter' },
  { value: 'LastYear', label: 'Last Year' },
];

const TIPS = [
  {
    key: 'late-fee-system',
    priority: 1,
    icon: 'timer',
    title: 'New: Late Fee System',
    body: 'Automatically calculate late fees on overdue invoices — fixed, percentage or daily interest. Configure it in Settings → Late Fee Settings.',
  },
  {
    key: 'custom-roles',
    priority: 2,
    icon: 'shield-tick',
    title: 'New: Custom Roles & Permissions',
    body: 'Create fine-grained roles with object-level access control. Go to Settings → Users → Custom Roles to get started.',
  },
  {
    key: 'backup-restore',
    priority: 3,
    icon: 'archive-tick',
    title: 'New: Backup & Restore',
    body: 'Export all your business data as a ZIP backup and restore from it anytime. Find it in Settings → Data Management.',
  },
  {
    key: 'period-comparison',
    priority: 4,
    icon: 'chart-line-up',
    title: 'Period Comparison in Reports',
    body: 'P&L, Balance Sheet, Cash Flow and more now show side-by-side prior period columns so you can spot trends instantly.',
  },
  {
    key: 'documents-module',
    priority: 5,
    icon: 'folder',
    title: 'New: Documents Module',
    body: 'Upload, manage and organise your business documents in one place. Track storage usage and attach files to records.',
  },
  {
    key: 'global-search',
    priority: 6,
    icon: 'magnifier',
    title: 'Improved Global Search',
    body: 'The search bar has been fully rewritten — faster results across transactions, contacts, items and more.',
  },
  {
    key: 'recycle-bin',
    priority: 7,
    icon: 'trash',
    title: 'Recycle Bin',
    body: 'Deleted records are kept in the Recycle Bin so you can restore anything you accidentally removed.',
  },
  {
    key: 'transaction-activity-log',
    priority: 8,
    icon: 'notepad-edit',
    title: 'Transaction Activity Log',
    body: 'Open any invoice to see a full log of emails and WhatsApp messages sent, including delivery status per recipient.',
  },
  {
    key: 'save-and-add-new',
    priority: 9,
    icon: 'add-item',
    title: 'Save & Add New',
    body: 'Save a transaction and immediately open a blank new one of the same type with the new "Save & Add New" button on every form.',
  },
  {
    key: 'redesigned-settings',
    priority: 10,
    icon: 'setting',
    title: 'Redesigned Settings',
    body: 'Settings are now grouped by area — Business, Sales, Users, Inventory, Data and more. Everything is easier to find.',
  },
  {
    key: 'auto-payment-reminders',
    priority: 11,
    icon: 'notification',
    title: 'Automatic Payment Reminders',
    body: "We've turned this on for you: overdue invoices now get automatic reminders. Manage the schedule in Settings → Sales & Invoicing → Payment Reminders.",
  },
  {
    key: 'business-summary-email',
    priority: 12,
    icon: 'sms',
    title: 'Business Summary Email',
    body: "We've turned this on for you: a summary of your business performance is now emailed on a schedule. Manage frequency in Settings → Business Summary.",
  },
  {
    key: 'manufacturing-module',
    priority: 13,
    icon: 'setting-4',
    title: 'New: Manufacturing Module',
    body: 'Define finished goods as Bills of Materials and track Work Orders from planning through completion, with stock and accounting updated automatically. Find it under Manufacturing in the sidebar.',
  },
  {
    key: 'projects-module',
    priority: 14,
    icon: 'tablet-book',
    title: 'New: Projects Module',
    body: 'Create projects for a client, tag transactions to them, and generate invoices from billing milestones. Track billing status and project profitability under Projects in the sidebar.',
  },
  {
    key: 'budgets-module',
    priority: 15,
    icon: 'calculator',
    title: 'New: Budgets Module',
    body: 'Set planned income and expense amounts per account for a financial year and compare them against live actuals with the Budget vs Actual report. Find it under Budgets in the sidebar.',
  },
  {
    key: 'customer-portal',
    priority: 16,
    icon: 'monitor-mobile',
    title: 'New: Customer Portal',
    body: 'Give customers a self-service portal to view invoices, pay online and check their account statement, branded with your logo and colours. Enable it in Settings → Customer Portal.',
  },
];

// ── Helpers ───────────────────────────────────────────────────────────────────

function pctChange(current: number, prior: number): number {
  if (prior === 0) return 0;
  return ((current - prior) / Math.abs(prior)) * 100;
}

function formatPeriodLabel(from: string | Date, to: string | Date): string {
  if (!from || !to) return '—';
  const timeZone = globalValue?.bizMaster?.bizDetails?.timeZone || 'UTC';
  const opts: Intl.DateTimeFormatOptions = { month: 'short', year: 'numeric', timeZone };
  const toDisplayDate = (s: string | Date): Date => s instanceof Date ? s : new Date(s);
  const f = toDisplayDate(from).toLocaleDateString('en-US', opts);
  const t = toDisplayDate(to).toLocaleDateString('en-US', opts);
  return f === t ? f : `${f} – ${t}`;
}

function buildReportFilter(predefined: string): ReportFilter {
  const df = new DateFilter();
  df.predefinedDateFilter = predefined;
  df.updateDates();
  return new ReportFilter({ dateFilter: df });
}

function fmtCompact(v: number): string {
  const abs = Math.abs(v);
  const neg = v < 0 ? '-' : '';
  if (abs >= 1_000_000) return `${neg}${(abs / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${neg}${(abs / 1_000).toFixed(1)}K`;
  return `${neg}${abs.toFixed(0)}`;
}

// ── PeriodSelect ──────────────────────────────────────────────────────────────

interface PeriodSelectProps {
  value: string;
  onChange: (v: string) => void;
  loading?: boolean;
}

const PeriodSelect: FC<PeriodSelectProps> = ({ value, onChange, loading }) => (
  <div className='d-flex align-items-center gap-2'>
    {loading && (
      <span
        className='spinner-border spinner-border-sm text-primary'
        style={{ width: '14px', height: '14px' }}
      />
    )}
    <select
      className='form-select form-select-sm'
      value={value}
      onChange={e => onChange(e.target.value)}
      disabled={loading}
      style={{ minWidth: '130px', width: 'auto', borderRadius: '10px' }}
    >
      {DATE_OPTIONS.map(opt => (
        <option key={opt.value} value={opt.value}>
          {opt.label.toTranslatedDisplay()}
        </option>
      ))}
    </select>
  </div>
);

// ── WhatsNewFloatingWidget ────────────────────────────────────────────────────
// Moved out of the header into a floating, animated pop-up (bottom-left, clear of
// the sidebar) — shows one unseen tip at a time. Dismissing marks it seen
// server-side (fetchSeenFeatures/markFeatureSeen, unchanged), so it won't
// resurface until a genuinely new tip is shipped and added to TIPS.

const SORTED_TIPS = [...TIPS].sort((a, b) => a.priority - b.priority);
const CAROUSEL_AUTO_ADVANCE_MS = 6000;
// Bootstrap theme colors only — each has a --bs-{color}-rgb var for alpha
// backgrounds and pre-defined light/dark-mode contrast, unlike a raw hex.
const CAROUSEL_ACCENT_COLORS = ['warning', 'primary', 'success', 'info', 'danger'];

const WhatsNewFloatingWidget: FC = () => {
  const [unseenKeys, setUnseenKeys] = useState<string[] | null>(null);
  const [cursor, setCursor] = useState(0);
  const [visible, setVisible] = useState(false);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    let active = true;
    fetchSeenFeatures()
      .then(seen => {
        if (!active) return;
        const seenSet = new Set(seen);
        setUnseenKeys(SORTED_TIPS.filter(t => !seenSet.has(t.key)).map(t => t.key));
      })
      .catch(() => {
        if (active) setUnseenKeys([]);
      });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (!unseenKeys || unseenKeys.length === 0) return;
    // Small delay so the pop-up slides/fades in after the dashboard has settled,
    // rather than popping in instantly on mount.
    const timer = setTimeout(() => setVisible(true), 500);
    return () => clearTimeout(timer);
  }, [unseenKeys]);

  // Re-armed on every cursor change (auto or manual) so a manual click/arrow/dot
  // always buys a full interval of reading time before the carousel moves again.
  useEffect(() => {
    if (!unseenKeys || unseenKeys.length <= 1 || paused) return;
    const timer = setInterval(() => {
      setCursor(c => (c + 1) % unseenKeys.length);
    }, CAROUSEL_AUTO_ADVANCE_MS);
    return () => clearInterval(timer);
  }, [unseenKeys, paused, cursor]);

  const activeIndex = unseenKeys && unseenKeys.length > 0
    ? ((cursor % unseenKeys.length) + unseenKeys.length) % unseenKeys.length
    : 0;
  // Re-rolled per tip shown (not per render) — each card that scrolls into view
  // gets its own random accent so the carousel reads as more eye-catching than a
  // single fixed color, without flickering while one tip is on screen.
  const accent = useMemo(
    () => CAROUSEL_ACCENT_COLORS[Math.floor(Math.random() * CAROUSEL_ACCENT_COLORS.length)],
    [activeIndex], // eslint-disable-line react-hooks/exhaustive-deps -- re-roll trigger, not a data dependency
  );

  if (!unseenKeys || unseenKeys.length === 0) return null;

  const tip = SORTED_TIPS.find(t => t.key === unseenKeys[activeIndex])!;

  const goTo = (index: number) => setCursor(index);
  const next = () => setCursor(c => (c + 1) % unseenKeys.length);
  const prev = () => setCursor(c => (c - 1 + unseenKeys.length) % unseenKeys.length);

  // Dismissing closes the whole carousel: every currently-unseen tip is marked seen, so
  // nothing resurfaces until a new tip (new key) is added to TIPS in a later release.
  const dismiss = () => {
    unseenKeys.forEach(key => {
      markFeatureSeen(key).catch(() => { /* best-effort; will resurface next load */ });
    });
    setVisible(false);
    // Let the exit transition play before actually unmounting the carousel.
    setTimeout(() => {
      setUnseenKeys([]);
      setCursor(0);
    }, 250);
  };

  return (
    <div
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      style={{
        position: 'fixed',
        left: 'calc(var(--bs-app-sidebar-width, 265px) + 24px)',
        bottom: 24,
        zIndex: 190,
        width: 360,
        maxWidth: 'calc(100vw - 48px)',
        background: 'var(--bs-body-bg, #fff)',
        border: `1px solid rgba(var(--bs-${accent}-rgb), .35)`,
        borderRadius: 16,
        boxShadow: `0 12px 32px rgba(var(--bs-${accent}-rgb), .28)`,
        padding: '16px 18px',
        transform: visible ? 'translateY(0)' : 'translateY(16px)',
        opacity: visible ? 1 : 0,
        transition: 'transform .35s ease, opacity .35s ease, border-color .3s ease, box-shadow .3s ease',
        pointerEvents: visible ? 'auto' : 'none',
      }}
    >
      <div className='d-flex align-items-start justify-content-between gap-2 mb-2'>
        <div className='d-flex align-items-center gap-2'>
          <span
            className='d-flex align-items-center justify-content-center flex-shrink-0 rounded-2'
            style={{ width: 28, height: 28, background: `rgba(var(--bs-${accent}-rgb), .18)`, color: `var(--bs-${accent})`, transition: 'background .3s ease, color .3s ease' }}
          >
            <KTIcon iconName={tip.icon} className='fs-4' />
          </span>
          <span
            className='fw-bolder text-uppercase'
            style={{ fontSize: '9px', letterSpacing: '.1em', padding: '2px 8px', borderRadius: '999px', background: `rgba(var(--bs-${accent}-rgb), .2)`, color: `var(--bs-${accent})`, transition: 'background .3s ease, color .3s ease' }}
          >
            {"What's New".toTranslatedDisplay()}
          </span>
        </div>
        <i
          className='bi bi-x-lg text-muted fs-8'
          style={{ cursor: 'pointer' }}
          onClick={dismiss}
          title={'Dismiss'.toTranslatedDisplay()}
        ></i>
      </div>
      <div className='fw-bold fs-7 mb-1'>{tip.title.toTranslatedDisplay()}</div>
      <div className='text-muted fs-8' style={{ lineHeight: 1.5 }}>{tip.body.toTranslatedDisplay()}</div>
      {unseenKeys.length > 1 && (
        <div className='d-flex align-items-center justify-content-center gap-3 mt-3'>
          <i
            className='bi bi-chevron-left text-muted fs-8'
            style={{ cursor: 'pointer' }}
            onClick={prev}
            role='button'
            aria-label={'Previous tip'.toTranslatedDisplay()}
          ></i>
          <div className='d-flex align-items-center gap-1'>
            {unseenKeys.map((k, i) => (
              <span
                key={k}
                onClick={() => goTo(i)}
                role='button'
                aria-label={`${'Go to tip'.toTranslatedDisplay()} ${i + 1}`}
                style={{
                  width: i === activeIndex ? 16 : 6,
                  height: 6,
                  borderRadius: 3,
                  cursor: 'pointer',
                  background: i === activeIndex ? `var(--bs-${accent})` : 'rgba(0,0,0,.15)',
                  transition: 'width .2s ease, background .2s ease',
                }}
              ></span>
            ))}
          </div>
          <i
            className='bi bi-chevron-right text-muted fs-8'
            style={{ cursor: 'pointer' }}
            onClick={next}
            role='button'
            aria-label={'Next tip'.toTranslatedDisplay()}
          ></i>
        </div>
      )}
    </div>
  );
};

// ── KpiCard ───────────────────────────────────────────────────────────────────

interface KpiCardProps {
  title: string;
  tooltip?: string;
  icon: string;
  current: number;
  prior: number;
  color: string;
  higherIsBetter?: boolean;
  isSuffix?: boolean;
  suffix?: string;
  onClick?: () => void;
  series?: number[];
}

// Turn-2 mock: KPI cards gain a per-KPI sparkline under the amount, tinted to match the
// card's own color rather than a single fixed accent — reuses the ApexCharts sparkline
// pattern from StatisticsWidget3.tsx instead of hand-rolling inline SVG.
const KpiSparkline: FC<{ data: number[]; color: string }> = ({ data, color }) => {
  const chartRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!chartRef.current || data.length < 2) return;
    const lineColor = getCSSVariableValue(`--bs-${color}`);
    const chart = new ApexCharts(chartRef.current, {
      series: [{ name: 'Trend', data }],
      chart: { type: 'area', height: 30, sparkline: { enabled: true }, animations: { enabled: false } },
      stroke: { curve: 'smooth', width: 1.6, colors: [lineColor] },
      fill: { type: 'gradient', gradient: { opacityFrom: 0.28, opacityTo: 0 }, colors: [lineColor] },
      tooltip: { enabled: false },
      colors: [lineColor],
    });
    chart.render();
    return () => chart.destroy();
  }, [data, color]);

  if (data.length < 2) return null;
  return <div ref={chartRef} className='mt-2' style={{ height: 30 }} />;
};

const KpiCard: FC<KpiCardProps> = ({
  title, tooltip, icon, current, prior, color,
  higherIsBetter = true, isSuffix = false, suffix = '%', onClick, series,
}) => {
  useBootstrapTooltips([tooltip]);
  const change = pctChange(current, prior);
  const isPositive = higherIsBetter ? change >= 0 : change <= 0;
  const formatted = isSuffix ? `${current.toFixed(2)}${suffix}` : formatCurrency(current);
  const priorFormatted = isSuffix ? `${prior.toFixed(2)}${suffix}` : formatCurrency(prior);

  return (
    <div
      className={`card h-100${onClick ? ' cursor-pointer' : ''}`}
      style={{ borderRadius: '14px' }}
      onClick={onClick}
    >
      <div className='card-body p-5'>
        <div className='d-flex align-items-center gap-2'>
          <div
            className={`d-flex align-items-center justify-content-center flex-shrink-0 rounded-2 bg-light-${color}`}
            style={{ width: 26, height: 26 }}
          >
            <KTIcon iconName={icon} className={`text-${color} fs-6`} />
          </div>
          <span className='text-muted fw-bolder fs-9 text-uppercase' style={{ letterSpacing: '.08em' }}>
            {title.toTranslatedDisplay()}
          </span>
          {tooltip && (
            <i
              className='bi bi-question-circle text-muted fs-9'
              data-bs-toggle='tooltip'
              data-bs-placement='top'
              title={tooltip.toTranslatedDisplay()}
              style={{ cursor: 'pointer' }}
              onClick={e => e.stopPropagation()}
            ></i>
          )}
        </div>

        <div className='d-flex align-items-baseline gap-2 mt-3 flex-wrap'>
          <span className='fw-bold text-gray-900' style={{ fontSize: '1.4rem', letterSpacing: '-.02em' }}>
            {formatted}
          </span>
          <span
            className='fw-bold'
            style={{
              fontSize: '11px', padding: '2px 7px', borderRadius: '999px',
              background: isPositive ? 'var(--bs-success-light, #e8fff3)' : 'var(--bs-danger-light, #ffe0e0)',
              color: isPositive ? 'var(--bs-success)' : 'var(--bs-danger)',
            }}
          >
            {change >= 0 ? '↑' : '↓'} {Math.abs(change).toFixed(1)}%
          </span>
        </div>

        <div className='text-muted fs-9 mt-2'>
          {'Prior'.toTranslatedDisplay()} <span>{priorFormatted}</span>
        </div>

        {series && <KpiSparkline data={series} color={color} />}
      </div>
    </div>
  );
};

// ── PLComparisonChartV3 ───────────────────────────────────────────────────────

interface PLChartProps {
  current: PeriodData;
  prior: PeriodData;
  currentLabel: string;
  priorLabel: string;
}

const PLComparisonChartV3: FC<PLChartProps> = ({ current, prior, currentLabel, priorLabel }) => {
  const chartRef = useRef<HTMLDivElement | null>(null);
  const { mode } = useThemeMode();

  const buildOptions = useCallback((): ApexOptions => {
    const months = current.profitnLossData.map(d => d.period.slice(0, 3));
    const borderColor = getCSSVariableValue('--bs-gray-200');
    const labelColor = getCSSVariableValue('--bs-gray-500');

    return {
      series: [
        { name: 'Income'.toTranslatedDisplay(), type: 'bar', data: current.profitnLossData.map(d => d.income) },
        { name: 'Expense'.toTranslatedDisplay(), type: 'bar', data: current.profitnLossData.map(d => d.expense) },
        { name: 'Income (Prior)'.toTranslatedDisplay(), type: 'line', data: prior.profitnLossData.map(d => d.income) },
        { name: 'Expense (Prior)'.toTranslatedDisplay(), type: 'line', data: prior.profitnLossData.map(d => d.expense) },
      ],
      chart: { type: 'line', height: 260, toolbar: { show: false }, fontFamily: 'inherit' },
      colors: ['#4C8DFF', '#F0563F', '#A9C7FF', '#F7ADA0'],
      stroke: { width: [0, 0, 2, 2], dashArray: [0, 0, 5, 5], curve: 'smooth' },
      plotOptions: { bar: { columnWidth: '40%', borderRadius: 4 } },
      dataLabels: { enabled: false },
      xaxis: {
        categories: months,
        axisBorder: { show: false },
        axisTicks: { show: false },
        labels: { style: { fontSize: '11px', colors: labelColor } },
      },
      yaxis: { labels: { formatter: fmtCompact, style: { fontSize: '11px', colors: labelColor } } },
      legend: { position: 'top', horizontalAlign: 'right', fontSize: '11.5px', markers: { size: 8 } },
      tooltip: { shared: true, intersect: false, y: { formatter: (v: number) => formatCurrency(v) } },
      grid: { strokeDashArray: 4, borderColor },
    };
  }, [current, prior, mode]);

  useEffect(() => {
    if (!chartRef.current || !current.profitnLossData.length) return;
    const chart = new ApexCharts(chartRef.current, buildOptions());
    chart.render();
    return () => chart.destroy();
  }, [buildOptions]);

  return (
    <div className='card h-100' style={{ borderRadius: '14px' }}>
      <div className='card-body p-6'>
        <div className='d-flex align-items-start justify-content-between flex-wrap gap-2'>
          <div>
            <div className='fw-bold fs-6'>{'Profit & Loss'.toTranslatedDisplay()}</div>
            <div className='text-muted fs-8 mt-1'>
              {currentLabel}<span className='mx-2 text-gray-300'>{'vs'.toTranslatedDisplay()}</span>{priorLabel}
            </div>
          </div>
        </div>
        {current.profitnLossData.length === 0 ? (
          <div className='d-flex align-items-center justify-content-center text-muted fs-7' style={{ height: '260px' }}>
            {'No data for this period'.toTranslatedDisplay()}
          </div>
        ) : (
          <div ref={chartRef} className='mt-4' style={{ height: '260px' }} />
        )}
      </div>
    </div>
  );
};

// ── CashFlowComparisonChartV3 ─────────────────────────────────────────────────

const CashFlowComparisonChartV3: FC<PLChartProps> = ({ current, prior, currentLabel, priorLabel }) => {
  const chartRef = useRef<HTMLDivElement | null>(null);
  const { mode } = useThemeMode();

  const buildOptions = useCallback((): ApexOptions => {
    const months = current.cashFlowData.map(d => d.period.slice(0, 3));
    const primary = getCSSVariableValue('--bs-primary');
    const gray = getCSSVariableValue('--bs-gray-400');
    const borderColor = getCSSVariableValue('--bs-gray-200');
    const labelColor = getCSSVariableValue('--bs-gray-500');

    return {
      series: [
        { name: 'Current Period'.toTranslatedDisplay(), data: current.cashFlowData.map(d => d.balance) },
        { name: 'Prior Period'.toTranslatedDisplay(), data: prior.cashFlowData.map(d => d.balance) },
      ],
      chart: { type: 'line', height: 260, toolbar: { show: false }, fontFamily: 'inherit' },
      colors: [primary, gray],
      stroke: { width: [3, 2], curve: 'smooth', dashArray: [0, 6] },
      dataLabels: { enabled: false },
      xaxis: {
        categories: months,
        axisBorder: { show: false },
        axisTicks: { show: false },
        labels: { style: { fontSize: '11px', colors: labelColor } },
      },
      yaxis: { labels: { formatter: fmtCompact, style: { fontSize: '11px', colors: labelColor } } },
      legend: { position: 'top', horizontalAlign: 'right', fontSize: '11.5px', markers: { size: 8 } },
      tooltip: { shared: true, intersect: false, y: { formatter: (v: number) => formatCurrency(v) } },
      grid: { strokeDashArray: 4, borderColor },
      markers: { size: [4, 3], strokeWidth: 2 },
    };
  }, [current, prior, mode]);

  useEffect(() => {
    if (!chartRef.current || !current.cashFlowData.length) return;
    const chart = new ApexCharts(chartRef.current, buildOptions());
    chart.render();
    return () => chart.destroy();
  }, [buildOptions]);

  const closing = current.cashFlowData.length ? current.cashFlowData[current.cashFlowData.length - 1].balance : 0;

  return (
    <div className='card h-100' style={{ borderRadius: '14px' }}>
      <div className='card-body p-6'>
        <div className='d-flex align-items-start justify-content-between flex-wrap gap-2'>
          <div>
            <div className='fw-bold fs-6'>{'Cash Flow'.toTranslatedDisplay()}</div>
            <div className='text-muted fs-8 mt-1'>
              {'Cumulative net cash'.toTranslatedDisplay()} · {currentLabel}<span className='mx-2 text-gray-300'>{'vs'.toTranslatedDisplay()}</span>{priorLabel}
            </div>
          </div>
          <div className='text-end'>
            <div className='fw-bold fs-5'>{formatCurrency(closing)}</div>
            <div className='text-muted fw-bold fs-9 text-uppercase' style={{ letterSpacing: '.06em' }}>{'Closing'.toTranslatedDisplay()}</div>
          </div>
        </div>
        {current.cashFlowData.length === 0 ? (
          <div className='d-flex align-items-center justify-content-center text-muted fs-7' style={{ height: '260px' }}>
            {'No data for this period'.toTranslatedDisplay()}
          </div>
        ) : (
          <div ref={chartRef} className='mt-4' style={{ height: '260px' }} />
        )}
      </div>
    </div>
  );
};

// ── NeedsAttentionWidget ──────────────────────────────────────────────────────
// Turn-2 mock: severity-grouped queue (Blocking / Due soon / Tidy up) instead of a single
// "Overdue (AR)" row inside Balances. Backed by GET /reporting/getDashboardNeedsAttention
// (MBAI-430), which combines the existing overdue/due-soon invoice summary (MBAI-586) with
// new due-soon-bill and uncategorized-transaction counts computed server-side.

interface NeedsAttentionRowProps {
  icon: string;
  color: string;
  title: string;
  subtitle: string;
  amount: number;
  onClick?: () => void;
}

const NeedsAttentionRow: FC<NeedsAttentionRowProps> = ({ icon, color, title, subtitle, amount, onClick }) => (
  <div
    className={`d-flex align-items-center gap-3 py-3 px-4${onClick ? ' cursor-pointer' : ''}`}
    style={{ borderLeft: `3px solid var(--bs-${color})` }}
    onClick={onClick}
  >
    <span
      className={`d-flex align-items-center justify-content-center flex-shrink-0 rounded-2 bg-light-${color}`}
      style={{ width: 30, height: 30 }}
    >
      <KTIcon iconName={icon} className={`text-${color} fs-4`} />
    </span>
    <div className='flex-grow-1 min-w-0'>
      <div className='fw-bold fs-7'>{title}</div>
      <div className='text-muted fs-9 text-truncate'>{subtitle}</div>
    </div>
    <div className={`fw-bold fs-7 flex-shrink-0 text-${color}`}>
      {formatCurrency(amount)}
    </div>
  </div>
);

const NeedsAttentionSkeleton: FC = () => (
  <div className='card h-100 w-100' style={{ borderRadius: '14px', minHeight: 240 }}>
    <div className='card-body p-0'>
      <div className='px-6 pt-5 pb-3'>
        <span className='fw-bold fs-6'>{'Needs attention'.toTranslatedDisplay()}</span>
      </div>
      {[0, 1, 2].map(i => (
        <div key={i} className='d-flex align-items-center gap-3 py-3 px-4'>
          <span className='rounded-2 bg-light flex-shrink-0' style={{ width: 30, height: 30 }} />
          <div className='flex-grow-1'>
            <div className='bg-light rounded mb-2' style={{ height: 12, width: '60%' }} />
            <div className='bg-light rounded' style={{ height: 9, width: '40%' }} />
          </div>
          <div className='bg-light rounded' style={{ height: 12, width: 50 }} />
        </div>
      ))}
    </div>
  </div>
);

interface NeedsAttentionWidgetProps {
  data: DashboardNeedsAttentionDTO | undefined;
  refreshing?: boolean;
}

const NeedsAttentionWidget: FC<NeedsAttentionWidgetProps> = ({ data, refreshing }) => {
  const navigate = useNavigate();
  if (!data) return refreshing ? <NeedsAttentionSkeleton /> : null;

  const blocking = data.overdueInvoiceCount > 0 ? [{
    icon: 'information-5', title: `${data.overdueInvoiceCount} ${'invoices overdue'.toTranslatedDisplay()}`,
    subtitle: 'Unpaid past their due date'.toTranslatedDisplay(), amount: data.overdueInvoiceTotal,
    onClick: () => navigate('/reports/customer-aging-summary'),
  }] : [];

  const dueSoon = [
    ...(data.dueSoonInvoiceCount > 0 ? [{
      icon: 'time', title: `${data.dueSoonInvoiceCount} ${'invoices due soon'.toTranslatedDisplay()}`,
      subtitle: `${'Due within'.toTranslatedDisplay()} ${data.dueSoonDays} ${'days'.toTranslatedDisplay()}`, amount: data.dueSoonInvoiceTotal,
      onClick: () => navigate('/reports/invoice-report'),
    }] : []),
    ...(data.dueSoonBillCount > 0 ? [{
      icon: 'time', title: `${data.dueSoonBillCount} ${'bills due soon'.toTranslatedDisplay()}`,
      subtitle: `${'Due within'.toTranslatedDisplay()} ${data.dueSoonDays} ${'days'.toTranslatedDisplay()}`, amount: data.dueSoonBillTotal,
    }] : []),
  ];

  const tidyUp = data.uncategorizedCount > 0 ? [{
    icon: 'category', title: `${data.uncategorizedCount} ${'transactions to categorize'.toTranslatedDisplay()}`,
    subtitle: 'Bank feed transactions awaiting review'.toTranslatedDisplay(), amount: data.uncategorizedTotal,
    onClick: () => navigate('/banking'),
  }] : [];

  const allClear = blocking.length === 0 && dueSoon.length === 0 && tidyUp.length === 0;

  return (
    <div className='card h-100 w-100' style={{ borderRadius: '14px', minHeight: 240 }}>
      <div className='card-body p-0'>
        <div className='d-flex align-items-center gap-2 px-6 pt-5 pb-3'>
          <span className='fw-bold fs-6'>{'Needs attention'.toTranslatedDisplay()}</span>
          {!allClear && (
            <span
              className='fw-bolder text-uppercase text-danger'
              style={{ fontSize: '10px', letterSpacing: '.06em', padding: '2px 7px', borderRadius: '999px', background: 'var(--bs-danger-light, #ffe0e0)' }}
            >
              {blocking.length + dueSoon.length + tidyUp.length}
            </span>
          )}
          {refreshing && (
            <span
              className='spinner-border spinner-border-sm text-muted ms-auto'
              style={{ width: '12px', height: '12px' }}
              aria-label='Refreshing'
            />
          )}
        </div>

        {allClear ? (
          <div className='d-flex align-items-center gap-2 px-6 pb-5 text-success fs-7 fw-semibold'>
            <i className='bi bi-check-circle-fill' />
            {'All caught up — nothing needs attention right now.'.toTranslatedDisplay()}
          </div>
        ) : (
          <>
            {blocking.length > 0 && (
              <>
                <div className='px-6 py-2 bg-light-danger text-danger fw-bolder text-uppercase fs-9' style={{ letterSpacing: '.08em' }}>
                  {'Blocking'.toTranslatedDisplay()}
                </div>
                {blocking.map((row, i) => <NeedsAttentionRow key={`b-${i}`} color='danger' {...row} />)}
              </>
            )}
            {dueSoon.length > 0 && (
              <>
                <div className='px-6 py-2 bg-light-warning text-warning fw-bolder text-uppercase fs-9' style={{ letterSpacing: '.08em' }}>
                  {'Due soon'.toTranslatedDisplay()}
                </div>
                {dueSoon.map((row, i) => <NeedsAttentionRow key={`d-${i}`} color='warning' {...row} />)}
              </>
            )}
            {tidyUp.length > 0 && (
              <>
                <div className='px-6 py-2 bg-light text-muted fw-bolder text-uppercase fs-9' style={{ letterSpacing: '.08em' }}>
                  {'Tidy up'.toTranslatedDisplay()}
                </div>
                {tidyUp.map((row, i) => <NeedsAttentionRow key={`t-${i}`} color='primary' {...row} />)}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

// ── BalancePanelV3 ────────────────────────────────────────────────────────────

const BALANCE_ROWS = [
  { key: 'bankBalance' as const, label: 'Bank Balance', subtitle: 'All bank accounts', icon: 'bank', color: 'primary', path: '/reports/bank-statement',
    tooltip: 'Sum of all bank account balances as recorded in your bank/connected accounts.' },
  { key: 'myBooksBalance' as const, label: 'myBooks Balance', subtitle: 'General ledger balance', icon: 'abstract-26', color: 'info', path: '/reports/trial-balance',
    tooltip: 'The bank balance as calculated from your recorded transactions (general ledger). This may differ from Bank Balance if some transactions haven\'t been reconciled yet.' },
  { key: 'cashBalance' as const, label: 'Cash Balance', subtitle: 'Petty cash & cash in hand', icon: 'wallet', color: 'success', path: '/reports/account-transactions',
    tooltip: 'Balance held in petty cash / cash-in-hand accounts, separate from bank accounts.' },
  { key: 'overdue' as const, label: 'Overdue (AR)', subtitle: 'Unpaid customer invoices', icon: 'time', color: 'danger', path: '/reports/customer-aging-summary',
    tooltip: 'Total of unpaid customer invoices that are past their due date.', flag: 'Needs attention' },
];

const BalancePanelV3: FC<{ balances: BalancesDTO }> = ({ balances }) => {
  const navigate = useNavigate();
  useBootstrapTooltips([balances]);
  return (
    <div className='card h-100' style={{ borderRadius: '14px' }}>
      <div className='card-body p-6'>
        <div className='fw-bold fs-6'>{'Balances'.toTranslatedDisplay()}</div>
        <div className='text-muted fs-8 mt-1'>{'Live balance-sheet totals'.toTranslatedDisplay()}</div>
        <div className='d-flex flex-column mt-3'>
          {BALANCE_ROWS.map((row, i) => {
            const value = balances[row.key];
            const amountColor = row.key === 'overdue' || value < 0 ? 'danger' : 'gray-900';
            return (
              <div key={row.key}>
                <div
                  className='d-flex align-items-center gap-3 py-3 cursor-pointer'
                  onClick={() => navigate(row.path)}
                >
                  <div
                    className={`d-flex align-items-center justify-content-center flex-shrink-0 rounded-3 bg-light-${row.color}`}
                    style={{ width: 34, height: 34 }}
                  >
                    <KTIcon iconName={row.icon} className={`text-${row.color} fs-4`} />
                  </div>
                  <div className='flex-grow-1 d-flex align-items-center justify-content-between min-w-0'>
                    <div>
                      <div className='fw-bold text-gray-800 fs-7 d-flex align-items-center gap-2 flex-wrap'>
                        {row.label.toTranslatedDisplay()}
                        {row.flag && (
                          <span
                            className='fw-bolder text-uppercase text-danger'
                            style={{ fontSize: '9px', letterSpacing: '.06em', padding: '2px 6px', borderRadius: '999px', background: 'var(--bs-danger-light, #ffe0e0)' }}
                          >
                            {row.flag.toTranslatedDisplay()}
                          </span>
                        )}
                        <i
                          className='bi bi-question-circle text-muted fs-9'
                          data-bs-toggle='tooltip'
                          data-bs-placement='top'
                          title={row.tooltip.toTranslatedDisplay()}
                          style={{ cursor: 'pointer' }}
                          onClick={e => e.stopPropagation()}
                        ></i>
                      </div>
                      <div className='text-muted fs-9 mt-1'>{row.subtitle.toTranslatedDisplay()}</div>
                    </div>
                    <span className={`fw-bold fs-6 text-${amountColor} ms-3 flex-shrink-0`}>
                      {formatCurrency(value)}
                    </span>
                  </div>
                </div>
                {i < BALANCE_ROWS.length - 1 && <div className='separator separator-dashed' />}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// ── ExpenseCategoriesPanel ────────────────────────────────────────────────────
// Replaces the donut chart with a segmented bar + row list, per the new mock.

interface ExpenseCategoriesPanelProps {
  expenseCategories: { expenseCategory: string; amount: number }[];
}

const ExpenseCategoriesPanel: FC<ExpenseCategoriesPanelProps> = ({ expenseCategories }) => {
  const colors = [
    getCSSVariableValue('--bs-primary'),
    getCSSVariableValue('--bs-success'),
    getCSSVariableValue('--bs-warning'),
    getCSSVariableValue('--bs-danger'),
    getCSSVariableValue('--bs-info'),
    getCSSVariableValue('--bs-secondary'),
  ];

  const sorted = [...expenseCategories].sort((a, b) => b.amount - a.amount);
  const top5 = sorted.slice(0, 5);
  const rest = sorted.slice(5);
  const restTotal = rest.reduce((sum, c) => sum + c.amount, 0);
  const rows = restTotal > 0 ? [...top5, { expenseCategory: 'Others'.toTranslatedDisplay(), amount: restTotal }] : top5;
  const total = rows.reduce((sum, c) => sum + c.amount, 0);

  return (
    <div className='card h-100' style={{ borderRadius: '14px' }}>
      <div className='card-body p-6'>
        <div className='d-flex align-items-baseline justify-content-between gap-2'>
          <div>
            <div className='fw-bold fs-6'>{'Expense Categories'.toTranslatedDisplay()}</div>
            <div className='text-muted fs-8 mt-1'>
              {'Across'.toTranslatedDisplay()} {expenseCategories.length} {'expense categories'.toTranslatedDisplay()}
            </div>
          </div>
          <div className='fw-bold fs-6'>{formatCurrency(total)}</div>
        </div>

        {rows.length === 0 ? (
          <div className='d-flex align-items-center justify-content-center text-muted fs-7' style={{ height: '160px' }}>
            {'No data for this period'.toTranslatedDisplay()}
          </div>
        ) : (
          <>
            <div className='d-flex mt-4 gap-1' style={{ height: 10, borderRadius: 5, overflow: 'hidden' }}>
              {rows.map((c, i) => (
                <span key={c.expenseCategory} style={{ flex: Math.max(c.amount, 0.001), background: colors[i % colors.length] }} />
              ))}
            </div>
            <div className='d-flex flex-column mt-3'>
              {rows.map((c, i) => {
                const pct = total > 0 ? (c.amount / total) * 100 : 0;
                return (
                  <div
                    key={c.expenseCategory}
                    className='d-flex align-items-center gap-3 py-2'
                    style={{ borderBottom: i === rows.length - 1 ? 'none' : '1px solid var(--bs-gray-200)' }}
                  >
                    <span className='flex-shrink-0 rounded-1' style={{ width: 9, height: 9, background: colors[i % colors.length] }} />
                    <span className='flex-grow-1 min-w-0 fs-7 fw-semibold text-truncate'>{c.expenseCategory}</span>
                    <span className='text-muted fs-9'>{pct.toFixed(1)}%</span>
                    <span className='fw-bold fs-7' style={{ minWidth: 84, textAlign: 'right' }}>
                      {formatCurrency(c.amount)}
                    </span>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ── BudgetTrackerV3 ───────────────────────────────────────────────────────────
// Turn-2 mock: replaces the three separate Income/Expense/Net-Profit progress rows with
// (a) one "Overall spend" bar that shows an over-budget overflow explicitly instead of the
// old Math.min(...,100) clamp silently hiding it (the "1789% bars"/"of ₹0.00" bugs the mock
// calls out), and (b) a by-category breakdown sourced from the existing vs-actual endpoint
// (same data BudgetVsActualReport.tsx already renders in full) instead of just the top-3
// overspent accounts BudgetDashboardWidget exposes.

const OverallSpendBar: FC<{ actual: number; budget: number }> = ({ actual, budget }) => {
  if (budget <= 0) {
    return (
      <div className='fs-8 text-muted fw-semibold'>
        {actual > 0
          ? `${formatCurrency(actual)} ${'spent · no expense budget set'.toTranslatedDisplay()}`
          : 'No expense budget set'.toTranslatedDisplay()}
      </div>
    );
  }

  const pct = (actual / budget) * 100;
  const isOver = pct > 100;

  return (
    <div className='d-flex flex-column gap-2'>
      <div className='d-flex align-items-center justify-content-between'>
        <span className='fw-bold fs-7'>{'Overall spend'.toTranslatedDisplay()}</span>
        <span className={`fw-bold fs-8 ${isOver ? 'text-danger' : 'text-gray-700'}`}>
          {isOver
            ? `${(pct / 100).toFixed(1)}× ${'the budget'.toTranslatedDisplay()}`
            : `${pct.toFixed(1)}% ${'used'.toTranslatedDisplay()}`}
        </span>
      </div>
      <div className='position-relative' style={{ height: 10, borderRadius: 5, background: 'var(--bs-gray-200)', overflow: 'hidden' }}>
        {isOver ? (
          <>
            <span className='d-block h-100' style={{ width: '100%', background: 'var(--bs-danger)' }} />
            <span
              className='position-absolute top-0 bottom-0'
              style={{ left: `${Math.min(100 / (pct / 100), 100)}%`, width: 2, background: '#fff', opacity: 0.9 }}
            />
          </>
        ) : (
          <span
            className='d-block h-100'
            style={{ width: `${Math.max(pct, 0)}%`, background: pct >= 80 ? 'var(--bs-warning)' : 'var(--bs-primary)', transition: 'width .5s ease' }}
          />
        )}
      </div>
      <div className='d-flex justify-content-between fs-9 fw-semibold text-muted'>
        <span>{'Budget line at'.toTranslatedDisplay()} {formatCurrency(budget)}</span>
        <span>{'Spent'.toTranslatedDisplay()} {formatCurrency(actual)}</span>
      </div>
    </div>
  );
};

const BudgetCategoryRow: FC<{ row: BudgetVsActualRow }> = ({ row }) => {
  const target = row.budgetTotal;
  const actual = row.actualTotal;
  const pct = target > 0 ? (actual / target) * 100 : (actual > 0 ? 100 : 0);
  const isOver = target > 0 && actual > target;
  const tag = isOver
    ? { tone: 'danger', label: `${formatCurrency(actual - target)} ${'over'.toTranslatedDisplay()}` }
    : target > 0 && pct >= 80
      ? { tone: 'warning', label: `${pct.toFixed(0)}% ${'used'.toTranslatedDisplay()}` }
      : { tone: 'success', label: target > 0 ? `${formatCurrency(target - actual)} ${'left'.toTranslatedDisplay()}` : 'No budget'.toTranslatedDisplay() };

  return (
    <div className='py-3' style={{ borderBottom: '1px solid var(--bs-gray-200)' }}>
      <div className='d-flex align-items-center justify-content-between gap-2 flex-wrap'>
        <span className='fw-bold fs-7'>{row.accountName}</span>
        <div className='d-flex align-items-center gap-2'>
          <span className='fs-9 fw-semibold text-muted'>
            {formatCurrency(actual)} <span>{'of'.toTranslatedDisplay()}</span> {formatCurrency(target)}
          </span>
          <span className={`badge badge-light-${tag.tone} fw-bold fs-9`}>{tag.label}</span>
        </div>
      </div>
      <div className='mt-2' style={{ height: 8, borderRadius: 5, background: 'var(--bs-gray-200)', overflow: 'hidden' }}>
        <span
          className='d-block h-100'
          style={{ width: `${Math.min(pct, 100)}%`, background: `var(--bs-${tag.tone})`, transition: 'width .5s ease' }}
        />
      </div>
    </div>
  );
};

const BudgetTrackerV3: FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<BudgetDashboardWidget | null | 'loading' | 'error'>('loading');
  const [categoryRows, setCategoryRows] = useState<BudgetVsActualRow[]>([]);

  useEffect(() => {
    fetchBudgetDashboardWidget()
      .then(d => setData(d ?? null))
      .catch(() => setData('error'));
  }, []);

  useEffect(() => {
    if (data === 'loading' || data === 'error' || !data?.budgetId) return;
    fetchBudgetVsActual(data.budgetId)
      .then(result => {
        const rows = (result?.rows ?? [])
          .filter(r => !r.isSection && !r.isTotal && r.accountType !== 'Income' && r.accountType !== 'OtherIncome')
          .sort((a, b) => b.actualTotal - a.actualTotal)
          .slice(0, 5);
        setCategoryRows(rows);
      })
      .catch(() => setCategoryRows([]));
  }, [data]);

  if (data === 'loading' || data === 'error') return null;

  // No Active budget for the current FY: show a prompt rather than silently dropping the widget
  if (!data) {
    return (
      <div className='card' style={{ borderRadius: '14px' }}>
        <div className='card-body p-6 d-flex align-items-center justify-content-between gap-3 flex-wrap'>
          <div>
            <div className='fw-bold fs-6'>{'Budget Tracker'.toTranslatedDisplay()}</div>
            <div className='text-muted fs-8 mt-1'>{'No active budget for this financial year.'.toTranslatedDisplay()}</div>
          </div>
          <button className='btn btn-sm btn-primary' onClick={() => navigate('/budgets/new')}>
            {'Create one →'.toTranslatedDisplay()}
          </button>
        </div>
      </div>
    );
  }

  const netVariancePct = data.netBudgetProfit !== 0
    ? ((data.netActualProfit - data.netBudgetProfit) / Math.abs(data.netBudgetProfit)) * 100
    : 0;
  const breachedCount = (data.ytdActualExpense > data.totalBudgetExpense ? 1 : 0) + (data.netActualProfit < data.netBudgetProfit ? 1 : 0);

  return (
    <div className='card' style={{ borderRadius: '14px' }}>
      <div className='card-body p-6'>
        <div className='d-flex align-items-center justify-content-between gap-3 flex-wrap'>
          <div>
            <div className='fw-bold fs-6'>{'Budget Tracker'.toTranslatedDisplay()}</div>
            <div className='text-muted fs-8 mt-1'>
              {'FY'.toTranslatedDisplay()} {data.financialYear} · {breachedCount} {'targets breached'.toTranslatedDisplay()}
            </div>
          </div>
          <div className='d-flex gap-2'>
            <button className='btn btn-sm btn-light' onClick={() => navigate(`/budgets/${data.budgetId}`)}>
              {'Edit budget'.toTranslatedDisplay()}
            </button>
            <button className='btn btn-sm btn-primary' onClick={() => navigate(`/budgets/vs-actual?budgetId=${data.budgetId}`)}>
              {'Full report'.toTranslatedDisplay()}
            </button>
          </div>
        </div>

        <div className='mt-5'>
          <OverallSpendBar actual={data.ytdActualExpense} budget={data.totalBudgetExpense} />
        </div>

        <div className='mt-6'>
          <div className='fw-bolder text-uppercase text-muted mb-2' style={{ fontSize: '9.5px', letterSpacing: '.08em' }}>
            {'By category'.toTranslatedDisplay()}
          </div>
          {categoryRows.length === 0 ? (
            <div className='text-muted fs-8'>{'No expense categories with activity yet.'.toTranslatedDisplay()}</div>
          ) : (
            <div>
              {categoryRows.map(row => <BudgetCategoryRow key={row.accountId} row={row} />)}
            </div>
          )}
        </div>

        <div className='d-flex align-items-center justify-content-between gap-3 mt-5 pt-4' style={{ borderTop: '1px solid var(--bs-gray-200)' }}>
          <div>
            <div className='fw-bolder fs-8'>{'Net profit vs plan'.toTranslatedDisplay()}</div>
          </div>
          <div className='d-flex align-items-center gap-2'>
            <span className='fw-bold fs-6'>{formatCurrency(data.netActualProfit)}</span>
            <span className={`badge badge-light-${data.netActualProfit >= data.netBudgetProfit ? 'success' : 'danger'} fw-bold fs-9`}>
              {data.netActualProfit >= data.netBudgetProfit ? '+' : ''}{netVariancePct.toFixed(1)}%
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── CreateMenuButton ──────────────────────────────────────────────────────────
// Replaces the single "New invoice" button — a "Create" dropdown covering the
// major record types, so the header doesn't need a button per transaction type.

const CREATE_MENU_ITEMS = [
  { label: 'Invoice', icon: 'document', path: '/transactions/create-invoice' },
  { label: 'Expense', icon: 'two-credit-cart', path: '/transactions/create-expense' },
  { label: 'Ledger', icon: 'bill', path: '/transactions/create-journal' },
  { label: 'Contact', icon: 'user', path: '/contacts/create-contact' },
  { label: 'Item', icon: 'add-item', path: '/items/create-item' },
  { label: 'Business Transaction', icon: 'tablet-book', path: '/transactions/create-business-transaction' },
];

const CreateMenuButton: FC = () => {
  const navigate = useNavigate();
  return (
    <Dropdown>
      <Dropdown.Toggle as='button' className='btn btn-sm btn-primary' id='dashboard-create-menu'>
        {'Create'.toTranslatedDisplay()}
      </Dropdown.Toggle>
      <Dropdown.Menu align='end'>
        {CREATE_MENU_ITEMS.map(item => (
          <Dropdown.Item key={item.path} onClick={() => navigate(item.path)}>
            <KTIcon iconName={item.icon} className='fs-4 me-2 text-muted' />
            {item.label.toTranslatedDisplay()}
          </Dropdown.Item>
        ))}
      </Dropdown.Menu>
    </Dropdown>
  );
};

// ── DashboardHeaderV3 ─────────────────────────────────────────────────────────

interface DashboardHeaderProps {
  currentLabel: string;
  priorLabel: string;
  selectedPeriod: string;
  onPeriodChange: (v: string) => void;
  loading: boolean;
}

const DashboardHeaderV3: FC<DashboardHeaderProps> = ({
  currentLabel, priorLabel, selectedPeriod, onPeriodChange, loading,
}) => {
  return (
    <div className='d-flex align-items-center justify-content-between flex-wrap gap-3 mb-5'>
      <div>
        <h2 className='fw-bold text-gray-900 mb-2'>{'Dashboard'.toTranslatedDisplay()}</h2>
        <div className='d-flex align-items-center gap-3 text-muted fs-8 flex-wrap'>
          <span>
            <span className='fw-semibold text-gray-700'>{'Current:'.toTranslatedDisplay()}</span> {currentLabel}
          </span>
          <span className='text-gray-300'>|</span>
          <span>
            <span className='fw-semibold text-gray-700'>{'Prior:'.toTranslatedDisplay()}</span> {priorLabel}
          </span>
        </div>
      </div>
      <div className='d-flex align-items-center gap-2 flex-shrink-0'>
        <PeriodSelect value={selectedPeriod} onChange={onPeriodChange} loading={loading} />
        <CreateMenuButton />
      </div>
    </div>
  );
};

// ── DashboardPageV3 (display) ─────────────────────────────────────────────────

interface DashboardPageV3Props {
  data: DashboardResponseV2 | undefined;
  needsAttention: DashboardNeedsAttentionDTO | undefined;
  needsAttentionRefreshing: boolean;
  subscription: SubscriptionData | undefined;
  selectedPeriod: string;
  onPeriodChange: (v: string) => void;
  loading: boolean;
}

const DashboardPageV3: FC<DashboardPageV3Props> = ({
  data, needsAttention, needsAttentionRefreshing, subscription, selectedPeriod, onPeriodChange, loading,
}) => {
  const navigate = useNavigate();

  const currentLabel = formatPeriodLabel(data?.current.fromDate ?? '', data?.current.toDate ?? '');
  const priorLabel = formatPeriodLabel(data?.prior.fromDate ?? '', data?.prior.toDate ?? '');

  const empty: PeriodData = {
    fromDate: '', toDate: '',
    profitnLossData: [], cashFlowData: [], expenseCategories: [],
    kpis: { sales: 0, expense: 0, operatingPM: 0, netProfit: 0 },
  };

  const current = data?.current ?? empty;
  const prior = data?.prior ?? empty;
  const balances = data?.balances ?? { bankBalance: 0, myBooksBalance: 0, cashBalance: 0, overdue: 0 };

  const salesSeries = current.profitnLossData.map(d => d.income);
  const expenseSeries = current.profitnLossData.map(d => d.expense);
  const netProfitSeries = current.profitnLossData.map(d => d.netPL);
  const marginSeries = current.profitnLossData.map(d => (d.income > 0 ? (d.netPL / d.income) * 100 : 0));

  const drilldownQuery = (from: string | Date, to: string | Date) => {
    const f = from instanceof Date ? from.toISOString() : from;
    const t = to instanceof Date ? to.toISOString() : to;
    return `from=${encodeURIComponent(f)}&to=${encodeURIComponent(t)}&predefinedDateFilter=${encodeURIComponent(selectedPeriod)}`;
  };

  const { bizSettings } = useAppContext();
  const effectiveEdition = getEffectiveEdition(
    subscription?.currentPlan?.edition ?? 'Free',
    subscription?.currentPlan?.status ?? 'inactive',
    subscription?.currentPlan?.isTrial ?? false,
  );
  // Budgets is gated by edition AND the per-business "Enable Budgets" switch (Settings → Budgets Module)
  const showBudgetTracker = canUseModule('Budgets', effectiveEdition) && !!bizSettings?.budgetSettings?.enabled;
  const showMarketing = brandSettings.showMarketingWidget;

  return (
    <>
      <ToolbarWrapper />
      <WhatsNewFloatingWidget />
      <Content>
        <div className='container-xxl'>
          <OnboardingHelper className='mb-8' />

          <DashboardHeaderV3
            currentLabel={currentLabel}
            priorLabel={priorLabel}
            selectedPeriod={selectedPeriod}
            onPeriodChange={onPeriodChange}
            loading={loading}
          />

          {/* ── KPI row ── */}
          <div className='row g-4 mb-5'>
            <div className='col-6 col-xl-3'>
              <KpiCard
                title={'Sales'.toTranslatedDisplay()}
                tooltip={'Total invoiced sales for the selected period, compared to the prior period.'}
                icon='dollar'
                current={current.kpis.sales}
                prior={prior.kpis.sales}
                color='primary'
                series={salesSeries}
                onClick={() => navigate(`/reports/invoice-report?${drilldownQuery(current.fromDate, current.toDate)}`)}
              />
            </div>
            <div className='col-6 col-xl-3'>
              <KpiCard
                title={'Expenses'.toTranslatedDisplay()}
                tooltip={'Total recorded expenses for the selected period, compared to the prior period.'}
                icon='minus-circle'
                current={current.kpis.expense}
                prior={prior.kpis.expense}
                color='danger'
                higherIsBetter={false}
                series={expenseSeries}
                onClick={() => navigate(`/reports/expense-report?${drilldownQuery(current.fromDate, current.toDate)}`)}
              />
            </div>
            <div className='col-6 col-xl-3'>
              <KpiCard
                title={'Net Profit'.toTranslatedDisplay()}
                tooltip={'Sales minus expenses for the selected period (before tax).'}
                icon='chart-line-up'
                current={current.kpis.netProfit}
                prior={prior.kpis.netProfit}
                color='success'
                series={netProfitSeries}
                onClick={() => navigate(`/reports/profit-loss?${drilldownQuery(current.fromDate, current.toDate)}`)}
              />
            </div>
            <div className='col-6 col-xl-3'>
              <KpiCard
                title={'Profit Margin'.toTranslatedDisplay()}
                tooltip={'Net profit as a percentage of sales for the selected period.'}
                icon='percentage'
                current={current.kpis.operatingPM}
                prior={prior.kpis.operatingPM}
                color='info'
                isSuffix
                suffix='%'
                series={marginSeries}
                onClick={() => navigate(`/reports/profit-loss?${drilldownQuery(current.fromDate, current.toDate)}`)}
              />
            </div>
          </div>

          {/* ── Needs Attention + AI Status Brief — two-column, AI Summary wider since it
               carries more content. AI Status Brief keeps the 'v3' verdict+evidence+actions
               layout so /dashboard (V2, same shared component) is unaffected. Per-item action
               buttons from the mock aren't wired here — 'improvements' is plain strings today
               with no target route, and stubbing fake buttons would be worse than none; that
               needs a backend/AI-prompt change as a follow-up. ── */}
          <div className='row g-5 mb-5'>
            <div className='col-xl-4'>
              <NeedsAttentionWidget data={needsAttention} refreshing={needsAttentionRefreshing} />
            </div>
            <div className='col-xl-8'>
              <AiStatusBriefCard variant='v3' locked={!canUseModule('AIAssistant', effectiveEdition)} />
            </div>
          </div>

          {/* ── P&L + Balances ── */}
          <div className='row g-5 mb-5'>
            <div className='col-xl-8'>
              <PLComparisonChartV3 current={current} prior={prior} currentLabel={currentLabel} priorLabel={priorLabel} />
            </div>
            <div className='col-xl-4'>
              <BalancePanelV3 balances={balances} />
            </div>
          </div>

          {/* ── Cash Flow + Expense Categories ── */}
          <div className='row g-5 mb-5'>
            <div className='col-xl-8'>
              <CashFlowComparisonChartV3 current={current} prior={prior} currentLabel={currentLabel} priorLabel={priorLabel} />
            </div>
            <div className='col-xl-4'>
              <ExpenseCategoriesPanel expenseCategories={current.expenseCategories} />
            </div>
          </div>

          {/* ── Budget Tracker (self-hides if no active budget or no access) ── */}
          {showBudgetTracker && (
            <div className='mb-5'>
              <BudgetTrackerV3 />
            </div>
          )}

          {/* ── Marketing widget — full width ── */}
          {showMarketing && <MarketingWidget className='' />}
        </div>
      </Content>
    </>
  );
};

// ── QuickAccessDashboardV3 ────────────────────────────────────────────────────

const QuickAccessDashboardV3: FC = () => (
  <>
    <ToolbarWrapper />
    <Content>
      <div className='container-xxl'>
        <OnboardingHelper className='mb-8' />
        <div className='mb-8'>
          <h2 className='fw-bold text-gray-900 mb-2'>{'Quick Access'.toTranslatedDisplay()}</h2>
          <p className='text-muted fs-6'>
            {'Select a module below to get started.'.toTranslatedDisplay()}
          </p>
        </div>
        <AccessibleTiles />
      </div>
    </Content>
  </>
);

// ── DashboardWrapperV3 (data fetching) ────────────────────────────────────────

const DashboardWrapperV3: FC = () => {
  const { updateToolbar } = useContext(ToolbarContext);
  const { roleAccess, subscriptionData } = useAppContext();
  const [selectedPeriod, setSelectedPeriod] = useState('ThisYear');
  const [data, setData] = useState<DashboardResponseV2 | undefined>();
  const [needsAttention, setNeedsAttention] = useState<DashboardNeedsAttentionDTO | undefined>();
  // Starts true so the skeleton is there from the very first render (no null → skeleton flash).
  const [needsAttentionRefreshing, setNeedsAttentionRefreshing] = useState(true);
  const [loading, setLoading] = useState(false);

  const hasFinancialAccess =
    roleAccess?.isAdmin === true ||
    canAccess(roleAccess?.permissions ?? [], 'FinancialReport', 'read');

  const cachePrefix = `dashV3_${globalValue?.bizMaster?.bizId ?? ''}`;

  const loadDashboard = useCallback(
    async (period: string) => {
      setLoading(true);
      const key = `${cachePrefix}_${period}`;
      const cached = localStorage.getItem(key);
      if (cached) {
        try {
          setData(JSON.parse(cached));
        } catch { /* stale / corrupt cache */ }
      }
      try {
        const reportFilter = buildReportFilter(period);
        const [current, prior, balances] = await Promise.all([
          fetchDashboardCurrentPeriod(reportFilter, true),
          fetchDashboardPriorPeriod(reportFilter, true),
          fetchDashboardBalances(reportFilter, true),
        ]);
        if (current && prior && balances) {
          const result: DashboardResponseV2 = { current, prior, balances };
          setData(result);
          localStorage.setItem(key, JSON.stringify(result));
        }
      } catch (e) {
        console.error('Dashboard v3 fetch error:', e);
      } finally {
        setLoading(false);
      }
    },
    [cachePrefix]
  );

  useEffect(() => {
    updateToolbar({
      ...defaultToolbarState,
      alwaysDisplay: true,
      showBranchPicker: true,
      showAIChat: true,
    });
    fetchItems(true);
    fetchContacts(true, true);
    fetchAccounts(true);
  }, []);

  useEffect(() => {
    if (hasFinancialAccess) {
      loadDashboard(selectedPeriod);
    }
  }, [selectedPeriod, loadDashboard, hasFinancialAccess]);

  // Needs Attention isn't period-scoped (it's always "as of now"), so it's fetched once,
  // independently of the period-driven loadDashboard above.
  useEffect(() => {
    if (!hasFinancialAccess) return;
    // Stale-while-revalidate: paint the last known result immediately, refresh in the
    // background, and keep showing the previous data if the refresh fails.
    const key = `${cachePrefix}_needsAttention`;
    try {
      const cached = localStorage.getItem(key);
      if (cached) setNeedsAttention(JSON.parse(cached));
    } catch { /* stale / corrupt cache */ }
    setNeedsAttentionRefreshing(true);
    fetchDashboardNeedsAttention(true)
      .then(result => {
        if (!result) return;
        setNeedsAttention(result);
        try { localStorage.setItem(key, JSON.stringify(result)); } catch { /* quota */ }
      })
      .catch(e => console.error('Dashboard v3 needs-attention fetch error:', e))
      .finally(() => setNeedsAttentionRefreshing(false));
  }, [hasFinancialAccess, cachePrefix]);

  return (
    <>
      <PageTitle showBizPicker />
      {hasFinancialAccess ? (
        <DashboardPageV3
          data={data}
          needsAttention={needsAttention}
          needsAttentionRefreshing={needsAttentionRefreshing}
          subscription={subscriptionData}
          selectedPeriod={selectedPeriod}
          onPeriodChange={setSelectedPeriod}
          loading={loading}
        />
      ) : (
        <QuickAccessDashboardV3 />
      )}
    </>
  );
};

export { DashboardWrapperV3 };
